import { useCallback } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import type { StudyArtifactAttentionAcknowledgmentReason } from '@digital-twins/shared-types'
import { useAuth } from '@/lib/config/auth-provider'
import { studyArtifactAttentionApi } from '@/lib/api/study-artifact-attention'

export const STUDY_ARTIFACT_ATTENTION_KEY = 'study-artifact-attention'

export function isStudyArtifactAttentionKey(key: unknown): key is Array<unknown> {
  return Array.isArray(key) && key[0] === STUDY_ARTIFACT_ATTENTION_KEY
}

/**
 * Server receipts are the attention state. Reading a thread, receiving an
 * event, or marking ordinary notifications read never acknowledges a receipt.
 */
export function useStudyArtifactAttention(threadToken?: string, options: { enabled?: boolean } = {}) {
  const { isAuthenticated, user } = useAuth()
  const { mutate: mutateCache } = useSWRConfig()
  const enabled = isAuthenticated && Boolean(user?.id) && options.enabled !== false
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? [STUDY_ARTIFACT_ATTENTION_KEY, user!.id, threadToken ?? null] : null,
    () => studyArtifactAttentionApi.list(threadToken),
    {
      dedupingInterval: 5_000,
      refreshInterval: 30_000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
    },
  )

  const acknowledge = useCallback(async (
    attentionToken: string,
    reason: StudyArtifactAttentionAcknowledgmentReason,
  ) => {
    if (!enabled) throw new Error('Sign in to acknowledge study attention.')
    const receipt = await studyArtifactAttentionApi.acknowledge(attentionToken, reason)
    await mutateCache(
      (key) => isStudyArtifactAttentionKey(key) && key[1] === user!.id,
      undefined,
      { revalidate: true },
    )
    return receipt
  }, [enabled, mutateCache, user?.id])

  return { items: enabled ? data?.items ?? [] : [], isLoading: enabled && isLoading, error, acknowledge, refresh: mutate }
}
